import { type ReactNode, useEffect } from 'react';
import {
  BadgeCheck,
  Check,
  ClipboardCheck,
  Inbox,
  PackageX,
  PhoneCall,
  Receipt,
  ShieldCheck,
  Undo2,
  X,
} from 'lucide-react';
import { describeDue, todayIsoDate, type SignalCategory, type SignalTask } from './types';

interface SignalCenterProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly tenantName: string;
  readonly tasks: readonly SignalTask[];
  readonly loading: boolean;
  readonly completing: string | null;
  readonly onOpenRoute: (route: string) => void;
  readonly onComplete: (taskId: string) => void;
}

const categoryMeta: Readonly<Record<SignalCategory, { readonly label: string; readonly icon: ReactNode }>> = {
  billing: { label: 'Abrechnung', icon: <Receipt size={16} /> },
  review: { label: 'Prüfung', icon: <ClipboardCheck size={16} /> },
  approval: { label: 'Freigabe', icon: <BadgeCheck size={16} /> },
  inspection: { label: 'UVV-Prüfung', icon: <ShieldCheck size={16} /> },
  follow_up: { label: 'Nachverfolgung', icon: <PhoneCall size={16} /> },
  return: { label: 'Rücknahme', icon: <Undo2 size={16} /> },
  spare_part: { label: 'Ersatzteil', icon: <PackageX size={16} /> },
};

/**
 * FORQ Signal — the task center (spec 2.2), not just the bell. Tenant-scoped, permission-aware
 * list of what needs attention; each task deep-links to its source record and can be completed
 * inline. Anchored under the topbar bell.
 */
export function SignalCenter({
  open,
  onClose,
  tenantName,
  tasks,
  loading,
  completing,
  onOpenRoute,
  onComplete,
}: SignalCenterProps) {
  useEffect(() => {
    if (!open) return undefined;
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const today = todayIsoDate();

  return (
    <div
      className="forq-signal"
      role="dialog"
      aria-modal="true"
      aria-label="FORQ Signal – Aufgabencenter"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="forq-signal__panel">
        <header className="forq-signal__head">
          <div>
            <span className="forq-eyebrow">Aufgabencenter · {tenantName}</span>
            <h2>FORQ Signal</h2>
          </div>
          <div className="forq-signal__head-meta">
            <span className="forq-signal__count">{tasks.length} offen</span>
            <button type="button" className="forq-icon-button" onClick={onClose} aria-label="Schließen">
              <X size={16} />
            </button>
          </div>
        </header>

        <div className="forq-signal__body">
          {loading ? (
            <p className="forq-signal__empty">Aufgaben werden geladen …</p>
          ) : tasks.length === 0 ? (
            <div className="forq-signal__empty forq-signal__empty--done">
              <Inbox size={28} />
              <strong>Alles erledigt.</strong>
              <span>Keine offenen Aufgaben für {tenantName}.</span>
            </div>
          ) : (
            <ul className="forq-signal__list">
              {tasks.map((task) => {
                const meta = categoryMeta[task.category];
                const due = describeDue(task.dueAt, today);
                return (
                  <li className="forq-signal__item" key={task.id}>
                    <button
                      type="button"
                      className={`forq-signal__open forq-signal__open--${task.priority}`}
                      onClick={() => onOpenRoute(task.route)}
                    >
                      <span className={`forq-signal__icon forq-signal__icon--${task.category}`}>{meta.icon}</span>
                      <span className="forq-signal__text">
                        <span className="forq-signal__title">{task.title}</span>
                        <span className="forq-signal__detail">{task.detail}</span>
                        <span className="forq-signal__chips">
                          <span className="forq-signal__chip">{meta.label}</span>
                          <span className="forq-signal__chip forq-signal__chip--muted">{task.sourceModule}</span>
                          <span className={`forq-signal__chip forq-signal__chip--due-${due.tone}`}>{due.label}</span>
                        </span>
                      </span>
                    </button>
                    <button
                      type="button"
                      className="forq-signal__done"
                      onClick={() => onComplete(task.id)}
                      disabled={completing === task.id}
                      aria-label={`„${task.title}" als erledigt markieren`}
                      title="Als erledigt markieren"
                    >
                      <Check size={15} />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <footer className="forq-signal__foot">
          <span>Aufgaben werden zweifach geprüft – Sichtbarkeit hier, Zugriff in der Datenbank (RLS).</span>
        </footer>
      </div>
    </div>
  );
}
